import { FC, useState, useMemo, useEffect, useCallback } from 'react';
import {CalendarTodayIcon} from '@ui/icons/material/CalendarToday';

import {MediaChevronDownIcon} from '@ui/icons/media/media-chevron-down';
import {TagIcon} from '@ui/icons/material/Tag';

import {GraphicEqIcon} from '@ui/icons/material/GraphicEq';
import {ArtistIcon} from '@player/custom/icons/ArtistIcon';

import {GenresIcon} from '@player/custom/icons/GenresIcon';
import {MusicNoteIcon} from '@ui/icons/material/MusicNote';

import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {ArtistsList, BPMList, GenresList, KeysList, LabelsList, ReleasedDateRange} from '@player/custom/filters';

import {DateRangeValue} from '@ui/forms/input-field/date/date-range-picker/date-range-value';
import {FormattedDateTimeRange} from '@ui/i18n/formatted-date-time-range';

import {useIsMobileMediaQuery} from '@ui/utils/hooks/is-mobile-media-query';
import {DateFormatPresets} from '@ui/i18n/formatted-date';

import { NormalizedModel, BPMValue } from '@player/types';
import {useBackendFilterUrlParams} from '@common/datatable/filters/backend-filter-url-params';

import { Button} from '@ui/buttons/button';
import { FilterOperator } from '@common/datatable/filters/backend-filter';

import { useUpdateEffect } from '@react-aria/utils';
import { dateRangeToAbsoluteRange, absoluteRangeToDateRange } from '@ui/forms/input-field/date/date-range-picker/form-date-range-picker';

import { FilterListValue } from '@common/datatable/filters/utils/encode-backend-filters';
import { decodeBackendFilters } from '@common/datatable/filters/utils/decode-backend-filters';

import { Channel } from '@common/channels/channel';

import clsx from 'clsx'
import { SortBy } from '@player/custom/filters/SortBy';

import { GridIcon, ListIcon } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router';

const monthDayFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
}

export interface TracksFilterProps {
    type: string
    channel: Channel
}

export const TracksFilter: FC<TracksFilterProps> = ({ type, channel }) => {
    const { replaceAll, encodedFilters } = useBackendFilterUrlParams()
    const isMobile = useIsMobileMediaQuery()

    const filters = useMemo(() => {
        if (!encodedFilters)
            return []

        return decodeBackendFilters(encodedFilters)
    }, [])
    const navigate = useNavigate()

    const [searchParams, setSearchParams] = useSearchParams()

    const isListview = useMemo(() => {
        return searchParams.get('view') === 'list'
    }, [searchParams])

    const [dateRange, setDateRange] = useState<DateRangeValue | null>(null)
    const [artists, setArtists] = useState<NormalizedModel[]>([])

    const [genres, setGenres] = useState<NormalizedModel[]>(channel?.slug === 'genre' && channel.restriction ? [channel.restriction] : [])
    const [labels, setLabels] = useState<NormalizedModel[]>([])

    const [bpm, setBpm] = useState<BPMValue | null>(null)
    const [keys, setKeys] = useState<string[]>([])

    const hasFilters = useMemo(() => {
        if (artists.length > 0)
            return true

        if (genres.length > 0 && channel?.config?.restriction !== 'genre')
            return true

        if (labels.length > 0)
            return true

        if (dateRange?.start && dateRange?.end)
            return true

        if (bpm?.from || bpm?.to)
            return true

        return keys.length > 0
    }, [filters, artists, keys, labels, genres, bpm, dateRange])

    const toggleView = useCallback(() => {
        if (!isListview) {
            setSearchParams((params) => {
                params.set('view', 'list')
                return params
            })
        } else {
            searchParams.delete('view')
        }

        setSearchParams(searchParams)

        navigate({
            search: `?${searchParams}`
        }, { replace: true })
    }, [isListview, searchParams, navigate, setSearchParams])

    const handleFilters = () => {
        const filters: FilterListValue[] = []

        if (artists?.length > 0) {
            filters.push({
                key: 'artists',
                operator: FilterOperator.has,
                value: artists
            })
        }

        if (genres?.length > 0 && channel?.restriction?.model_type !== 'genre') {
            filters.push({
                key: 'genres',
                operator: FilterOperator.has,
                value: genres
            })
        }

        if (labels.length > 0) {
            filters.push({
                key: 'tags',
                operator: FilterOperator.has,
                value: labels
            })
        }

        if (dateRange?.start && dateRange.end) {
            filters.push({
                key: 'release_date',
                operator: FilterOperator.between,
                value: dateRangeToAbsoluteRange(dateRange)
            })
        }

        if (bpm) {
            filters.push({
                key: 'bpm',
                operator: FilterOperator.between,
                value: {
                    start: bpm.from || 0,
                    end: bpm.to || 999
                }
            })
        }

        if (keys?.length > 0) {
            filters.push({
                key: 'key',
                // @ts-ignore
                operator: 'in',
                value: keys
            })
        }

        replaceAll(filters)
    }

    const clearAll = () => {
        setDateRange(null)
        setArtists([])

        if (channel?.config?.restriction !== 'genre')
            setGenres([])

        setLabels([])

        setBpm(null)
        setKeys([])

        replaceAll([])
    }

    useUpdateEffect(() => {
        handleFilters()
    }, [artists, genres, labels, dateRange, bpm, keys])

    useEffect(() => {
        if (filters?.length < 1)
            return

        for (const filter of filters) {
            if (filter.key === 'release_date' && filter.operator === FilterOperator.between) {
                const range = absoluteRangeToDateRange(filter.value) as DateRangeValue
                setDateRange(range)
            }

            if (filter.key === 'artists' && filter.operator === FilterOperator.has) {
                setArtists(filter.value)
            }

            if (filter.key === 'genres' && filter.operator === FilterOperator.has) {
                setGenres(filter.value)
            }

            if (filter.key === 'tags' && filter.operator === FilterOperator.has) {
                setLabels(filter.value)
            }

            if (filter.key === 'bpm' && filter.operator === FilterOperator.between) {
                setBpm({
                    from: filter.value.start,
                    to: filter.value.end
                })
            }

            // @ts-ignore
            if (filter.key === 'key' && filter.operator === 'in') {
                setKeys(filter.value)
            }
        }
    }, [])

    return (
        <div className="flex flex-col md:flex-row items-start gap-5 md:gap-10">
            <div className="flex flex-wrap items-center gap-5 md:gap-10">
                <DialogTrigger
                    type="popover"
                    triggerOnHover
                    onClose={value => {
                        if (value) {
                            setDateRange(value)
                        }
                    }}
                    mobileType="modal"
                >
                    <button
                        className={clsx(
                            'flex items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md pl-10 h-32',
                            {
                                'pr-10': !dateRange,
                                'pr-4': dateRange
                            }
                        )}
                    >
                        <CalendarTodayIcon className="w-16 h-16" />
                        <span>Release Date</span>

                        <MediaChevronDownIcon className="w-12 h-12" />

                        {dateRange && (
                            <>
                                <span className="h-20 w-1 bg-background/70"></span>
                                <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">
                                    <FormattedDateTimeRange
                                      start={dateRange.start}
                                      end={dateRange.end}
                                      options={
                                        isMobile
                                          ? monthDayFormat
                                          : DateFormatPresets.short
                                      }
                                    />
                                </span>
                            </>
                        )}
                    </button>

                    <ReleasedDateRange
                        value={dateRange}
                        onChange={(value) => {
                            setDateRange(value)
                        }}
                    />
                </DialogTrigger>

                {type === 'tracks' && (
                    <DialogTrigger
                        type="popover"
                        triggerOnHover
                        mobileType="modal"
                        returnFocusToTrigger={!isMobile}
                        moveFocusToDialog={!isMobile}
                    >
                        <button
                            className={clsx(
                                'flex items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md pl-10 h-32',
                                {
                                    'pr-10': !bpm,
                                    'pr-4': bpm
                                }
                            )}
                        >
                            <GraphicEqIcon className="w-16 h-16" />
                            <span>BPM</span>

                            <MediaChevronDownIcon className="w-12 h-12" />

                            {bpm && (bpm?.from > 0 || bpm?.to > 0) && (
                                <>
                                    <span className="h-20 w-1 bg-background/70"></span>
                                    <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{bpm?.from || 0}</span>
                                    -
                                    <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{bpm?.to || 999}</span>
                                </>
                            )}
                        </button>

                        <BPMList
                            selected={bpm}
                            onSelect={(payload) => setBpm(payload)}
                            onClear={() => setBpm(null)}
                        />
                    </DialogTrigger>
                )}

                <DialogTrigger
                    type="popover"
                    triggerOnHover
                    mobileType="modal"
                    returnFocusToTrigger={!isMobile}
                    moveFocusToDialog={!isMobile}
                >
                    <button
                        className={clsx(
                            'flex items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md pl-10 h-32',
                            {
                                'pr-10': !artists.length,
                                'pr-4': artists.length
                            }
                        )}
                    >
                        <ArtistIcon className="w-16 h-16" />
                        <span>Artists</span>

                        <MediaChevronDownIcon className="w-12 h-12" />

                        {artists?.length > 0 && (
                            <>
                                <span className="h-20 w-1 bg-background/70"></span>
                                <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{artists[0]?.display_name || artists[0]?.name}</span>
                                {artists?.length > 1 && <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">+{artists.length - 1}</span>}
                            </>
                        )}
                    </button>

                    <ArtistsList
                        genres={genres}
                        selected={artists}
                        onApply={(list) => setArtists(list)}
                        onClear={() => setArtists([])}
                        selectedLabels={labels}
                        dateRange={dateRange ? dateRangeToAbsoluteRange(dateRange) : null}
                    />
                </DialogTrigger>

                {type === 'tracks' && (
                    <DialogTrigger
                        type="popover"
                        triggerOnHover
                        mobileType="modal"
                    >
                        <button
                            className={clsx(
                                'flex items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md pl-10 h-32',
                                {
                                    'pr-10': !keys.length,
                                    'pr-4': keys.length
                                }
                            )}
                        >
                            <MusicNoteIcon className="w-16 h-16" />
                            <span>Key</span>

                            <MediaChevronDownIcon className="w-12 h-12" />

                            {keys?.length > 0 && (
                                <>
                                    <span className="h-20 w-1 bg-background/70"></span>
                                    <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{keys[0]}</span>
                                    {keys?.length > 1 && <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{keys[1]}</span>}
                                    {keys?.length > 2 && <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">+{keys.length - 2}</span>}
                                </>
                            )}
                        </button>

                        <KeysList
                            selected={keys}
                            onApply={(list) => setKeys(list)}
                            onClear={() => setKeys([])}
                            dateRange={dateRange ? dateRangeToAbsoluteRange(dateRange) : null}
                            artists={artists}
                            labels={labels}
                            genres={genres}
                        />
                    </DialogTrigger>
                )}

                {channel?.slug !== 'genre' && (
                    <DialogTrigger
                        type="popover"
                        triggerOnHover
                        mobileType="modal"
                        returnFocusToTrigger={!isMobile}
                        moveFocusToDialog={!isMobile}
                    >
                        <button
                            className={clsx(
                                'flex items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md pl-10 h-32',
                                {
                                    'pr-10': !genres.length,
                                    'pr-4': genres.length
                                }
                            )}
                        >
                            <GenresIcon className="w-16 h-16" />
                            <span>Genres</span>

                            <MediaChevronDownIcon className="w-12 h-12" />

                            {genres?.length > 0 && (
                                <>
                                    <span className="h-20 w-1 bg-background/70"></span>
                                    <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{genres[0]?.display_name || genres[0]?.name}</span>
                                    {genres?.length > 1 && <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">+{genres.length - 1}</span>}
                                </>
                            )}
                        </button>

                        <GenresList
                            selected={genres}
                            onApply={(list) => setGenres(list)}
                            onClear={() => setGenres([])}
                            artists={artists}
                            labels={labels}
                            dateRange={dateRange ? dateRangeToAbsoluteRange(dateRange) : null}
                        />
                    </DialogTrigger>
                )}

                <DialogTrigger
                    type="popover"
                    triggerOnHover
                    mobileType="modal"
                    returnFocusToTrigger={!isMobile}
                    moveFocusToDialog={!isMobile}
                >
                    <button
                        className={clsx(
                            'flex items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md pl-10 h-32',
                            {
                                'pr-10': !labels.length,
                                'pr-4': labels.length
                            }
                        )}
                    >
                        <TagIcon className="w-16 h-16" />
                        <span>Labels</span>

                        <MediaChevronDownIcon className="w-12 h-12" />

                        {labels?.length > 0 && (
                            <>
                                <span className="h-20 w-1 bg-background/70"></span>
                                <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">{labels[0]?.description || labels[0]?.display_name || labels[0]?.name}</span>
                                {labels?.length > 1 && <span className="block text-xs text-muted py-4 px-8 rounded bg-background/50">+{labels.length - 1}</span>}
                            </>
                        )}
                    </button>

                    <LabelsList
                        selected={labels}
                        onApply={(list) => setLabels(list)}
                        onClear={() => setLabels([])}
                        artists={artists}
                        genres={genres}
                        dateRange={dateRange ? dateRangeToAbsoluteRange(dateRange) : null}
                    />
                </DialogTrigger>

                {isMobile && (
                    <>
                        {type === 'album' && (
                            <button
                                className="flex shrink-0 md:ml-auto items-center gap-2 text-muted bg-hover/5 hover:bg-hover text-xs rounded-md h-32 py-3 px-3"
                                onClick={() => toggleView()}
                            >
                            <span
                                className={clsx('block transition-all duration-500 rounded-md p-5', {
                                    'bg-background': !isListview
                                })}
                            >
                                <GridIcon className="w-16 h-16" />
                            </span>
                                <span
                                    className={clsx('block transition-all duration-500 rounded-md p-5', {
                                        'bg-background': isListview
                                    })}
                                >
                                <ListIcon className="w-16 h-16" />
                            </span>
                            </button>
                        )}
                        <DialogTrigger
                            type="modal"
                            mobileType="modal"
                            placement="bottom-end"
                        >
                            <button
                                className="flex shrink-0 items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md px-10 h-32"
                            >
                                Sort by
                                <MediaChevronDownIcon className="w-12 h-12" />
                            </button>

                            <SortBy

                            />
                        </DialogTrigger>
                    </>
                )}

                {hasFilters && (
                    <Button
                        size="xs"
                        variant="flat"
                        onClick={() => clearAll()}
                        radius="rounded-md"
                        className="text-muted !h-32 hover:text-main"
                    >
                        Clear all
                    </Button>
                )}
            </div>

            {!isMobile && (
                <>
                    {type === 'album' && (
                        <button
                            className="flex shrink-0 md:ml-auto items-center gap-2 text-muted bg-hover/5 hover:bg-hover text-xs rounded-md h-32 py-3 px-3"
                            onClick={() => toggleView()}
                        >
                            <span
                                className={clsx('block transition-all duration-500 rounded-md p-5', {
                                    'bg-background': !isListview
                                })}
                            >
                                <GridIcon className="w-16 h-16" />
                            </span>
                            <span
                                className={clsx('block transition-all duration-500 rounded-md p-5', {
                                    'bg-background': isListview
                                })}
                            >
                                <ListIcon className="w-16 h-16" />
                            </span>
                        </button>
                    )}

                    <DialogTrigger
                        type="popover"
                        triggerOnHover
                        mobileType="modal"
                        placement="bottom-end"
                    >
                        <button
                            className={clsx('flex shrink-0 items-center gap-10 text-muted bg-hover/5 hover:bg-hover text-xs py-4 rounded-md px-10 h-32', {
                                'md:ml-auto': type !== 'album'
                            })}
                        >
                            Sort by
                            <MediaChevronDownIcon className="w-12 h-12" />
                        </button>

                        <SortBy

                        />
                    </DialogTrigger>
                </>
            )}
        </div>
    )
}
